import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { getImgEvent } from '../../../axios/Eventlog/EventLogApi';
import { StyledDataGrid } from '../../../components/StyledComponents';
import CustomPagination from '../CustomPagination';
import { Switch } from 'antd';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useLoading } from '../../../context/LoadingContext';
import ImageIcon from '@mui/icons-material/Image';
import { format, parse } from 'date-fns';
import CustomColumnsPanel from '../../../components/CustomColumnsPanel';
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useAuth } from '../../../context/AuthContext';
import { Tooltip } from '@mui/material';

dayjs.extend(utc);

const convertUTCToCompanyTime = (utcTime, timeZone) => {
   if (!utcTime || !timeZone) return "";

   const sign = timeZone.startsWith("-") ? -1 : 1;
   const [hh, mm] = timeZone.slice(1).split(":").map(Number);
   const offsetMinutes = sign * (hh * 60 + mm);

   return dayjs
      .utc(utcTime)
      .add(offsetMinutes, "minute");
};

const EventImagesGridInfoTable = ({ isShowErrorIMG, selectedDate, selectDevice, selectEndTime, selectStartTime, dispalyWeek, refreshData }) => {
   const { t } = useTranslation();
   const [dataIMG, setDataIMG] = useState([])
   const [page, setPage] = useState(0);
   const [pageSize, setPageSize] = useState(50);
   const [rowCount, setRowCount] = useState(0)
   const [show, hide] = useLoading();
   const columns = [];
   const rows = [];
   const is1440Up = window.innerWidth >= 1441;
   const { genaralSettingTimeZone } = useAuth();

   const getFromToByDateWithTZ = (date, timeZone) => {
      const base = dayjs(date).utcOffset(timeZone);

      return {
         from: base.startOf("day").utc().toISOString(),
         to: base.endOf("day").utc().toISOString(),
      };
   };

   const getUTCFromToByDate = (date) => {
      const from = new Date(Date.UTC(
         date.getFullYear(),
         date.getMonth(),
         date.getDate(),
         0, 0, 0
      )).toISOString();

      const to = new Date(Date.UTC(
         date.getFullYear(),
         date.getMonth(),
         date.getDate(),
         23, 59, 59
      )).toISOString();

      return { from, to };
   };

   useEffect(() => {
      const browserTZ = dayjs.tz.guess();
      const { from, to } = getFromToByDateWithTZ(selectedDate, browserTZ);

      const fetchData = async () => {
         const payload = {
            page: page + 1,
            record: pageSize,
            from, // ISO UTC
            to,   // ISO UTC
            isError: isShowErrorIMG,
            deviceId: selectDevice,
            startTime: selectStartTime,
            endTime: selectEndTime,
            langId: localStorage.getItem("i18nextLngId")
         }
         try {
            show();
            const response = await getImgEvent(payload)
            const imgList = response.data.data;
            const sortedImgList = [...imgList].sort((a, b) => {
               const timeA = convertUTCToCompanyTime(a.time, browserTZ);
               const timeB = convertUTCToCompanyTime(b.time, browserTZ);
               return timeB.valueOf() - timeA.valueOf();
            });

            setDataIMG(sortedImgList);
            setRowCount(response.data.pagination.totalElements);
         } catch (error) {
            if (error.response) {
               if (error.response.status !== 401) {
                  toast.error(t(error?.response.data));
               }
            } else {
               toast.error(t('common.network-timeout'));
            }
         } finally {
            hide();
         }
      };

      fetchData();
   }, [
      isShowErrorIMG,
      selectedDate,
      page,
      pageSize,
      selectDevice,
      selectStartTime,
      selectEndTime,
      refreshData,
      t
   ]);

   useEffect(() => {
      setPage(0);
   }, [
      selectedDate,
      isShowErrorIMG,
      selectDevice,
      selectStartTime,
      selectEndTime,
   ]);

   function AvatarCard({ fullName, time, imageBase64, eventTypeId }) {
      const isIn = eventTypeId === 1;
      const isOut = eventTypeId === 2;
      const localTime = dayjs.utc(time).local().format("HH:mm:ss");
      const displayName = fullName || t('common.unidentified-user');

      return (
         <div className="text-center  w-[173px]">
            <div className="border rounded-lg shadow p-1 h-[173px] w-[173px]">
               <img
                  src={imageBase64}
                  alt={displayName}
                  className="w-full h-full object-cover rounded-md"
               />
            </div>
            <Tooltip title={displayName} arrow placement="top">
               <div className="mt-2 font-semibold text-sm text-center truncate w-full">
                  {displayName}
               </div>
            </Tooltip>
            <div
               className={`flex items-center mt-1 text-sm text-gray-600 ${
                  eventTypeId ? "justify-between" : "justify-center"
               }`}
            >
               {eventTypeId && (
                  <div className="flex items-center gap-1">
                     <span
                        style={{
                           color: isIn ? '#16A34A' : '#2563EB',
                           fontWeight: 500,
                        }}
                     >
                        {isIn
                           ? t('common.in').toUpperCase()
                           : isOut
                              ? t('common.out').toUpperCase()
                              : ''}
                     </span>
                  </div>
               )}
               <div className="flex items-center gap-1">
                  <span>{localTime}</span>
               </div>
            </div>
         </div>
      );
   }

   const handleRowsPerPageChange = (newPageSize) => {
      // newPageSize ở đây tuỳ CustomPagination, có thể là event hoặc number
      const size =
         typeof newPageSize === "number"
            ? newPageSize
            : parseInt(newPageSize.target.value, 10);

      setPage((prevPage) => {
         // index của record đầu tiên đang xem
         const firstRowIndex = prevPage * pageSize;

         // page mới sao cho vẫn chứa record đó
         const newPage = Math.floor(firstRowIndex / size);

         // phòng trường hợp vượt quá số trang
         const maxPage = Math.max(
            0,
            Math.ceil(rowCount / size) - 1
         );

         return Math.min(newPage, maxPage);
      });

      setPageSize(size);
   };

   return (
      <div className='w-full h-[100%]'>
         <div className='py-2 main-scroll overflow-auto component-img' s>
            {dataIMG?.length === 0 ? (
               <div className="flex flex-col justify-center items-center h-[100%] text-bold gap-2">
                  <ImageIcon style={{ height: "55px", width: "55px", color: "#C0C0C0" }} />
                  {t('common.no-image')}
               </div>
            ) : (
               <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 2xl:grid-cols-6 3xl:grid-cols-8 gap-4">
                  {dataIMG?.map((item, index) => (
                     <AvatarCard key={index} {...item} />
                  ))}
               </div>
            )}
         </div>


         <div>
            <StyledDataGrid
               sx={{
                  '.MuiDataGrid-topContainer': {
                     display: 'none',

                  },
                  '.MuiDataGrid-main': {
                     border: 'none',
                     height: 20,
                  }

               }}
               slots={{
                  pagination: CustomPagination,
                  columnsPanel: CustomColumnsPanel,
               }}
               slotProps={{
                  pagination: {
                     page,
                     count: rowCount,
                     rowsPerPage: pageSize,
                     onPageChange: setPage,
                     onRowsPerPageChange: handleRowsPerPageChange,
                  },
               }}
               rows={rows}
               columns={columns}
               disableColumnMenu
               disableSelectionOnClick
               localeText={{
                  noRowsLabel: '',
               }}
            />
         </div>
      </div>
   );
};

export default EventImagesGridInfoTable;
